/**
 * Starting territories. Every faction floods outwards from its capital (multi-source Dijkstra);
 * stepping into rough terrain or across a river costs more, so fronts stall on ridges and river
 * lines and the resulting borders follow natural features. Each faction's growth is scaled by a
 * weight, and the weights are iterated until all factions hold a similar share of land, measured
 * half by raw area and half by usefulness (plains count fully, mountains and marsh much less).
 * Contiguity is guaranteed by the growth.
 */
import { NO_FACTION, Terrain } from '../core/types';
import { isLand, type MapContext, stageRng } from './context';
import { MinHeap } from './heap';
import { Noise } from './noise';
import { growRegions, type StepCost } from './paths';

/** Border-shaping cost of entering a hex, by terrain. */
const STEP_COST: Record<number, number> = {
  [Terrain.Plains]: 1,
  [Terrain.Urban]: 1,
  [Terrain.Forest]: 1.5,
  [Terrain.Hills]: 2,
  [Terrain.Marsh]: 2.5,
  [Terrain.Mountains]: 5,
};
const RIVER_COST = 3;
/** How much a hex is worth to a nation, by terrain. */
const LAND_VALUE: Record<number, number> = {
  [Terrain.Plains]: 1,
  [Terrain.Urban]: 1,
  [Terrain.Forest]: 0.8,
  [Terrain.Hills]: 0.75,
  [Terrain.Marsh]: 0.5,
  [Terrain.Mountains]: 0.35,
};

/** Feature size (in hexes) of the meander field that bends borders across open country. */
const MEANDER_SCALE = 4.5;
/** Step-cost multiplier range from the meander field: [1 − AMP, 1 + AMP]. */
const MEANDER_AMP = 0.7;

const meanders = new WeakMap<MapContext, Float64Array>();

/**
 * Low-frequency cost multiplier per hex. Without it, growth over uniform plains produces the
 * dead-straight bisector borders of a Voronoi diagram; with it, borders wander like historical
 * ones (salients, bulges) while still snapping to rivers and ridges.
 */
function meanderField(ctx: MapContext): Float64Array {
  let field = meanders.get(ctx);
  if (field) return field;
  const noise = new Noise(stageRng(ctx.seed, 'borders'));
  field = new Float64Array(ctx.n);
  for (let h = 0; h < ctx.n; h++) {
    const x = ctx.ux[h] / MEANDER_SCALE;
    const y = ctx.uy[h] / MEANDER_SCALE;
    const v = 0.65 * noise.simplex(x, y) + 0.35 * noise.simplex(x * 2.3 + 17, y * 2.3 - 9);
    field[h] = 1 + MEANDER_AMP * Math.max(-1, Math.min(1, v));
  }
  meanders.set(ctx, field);
  return field;
}

/** Terrain + river step cost shared by territory and region growth. */
export function borderCost(ctx: MapContext): StepCost {
  const { terrain, riverEdges } = ctx;
  const meander = meanderField(ctx);
  return (from, to, dir) =>
    STEP_COST[terrain[to]] * meander[to] + (riverEdges[from] & (1 << dir) ? RIVER_COST : 0);
}

/** Balance tolerance (max/min territory value) below which weighted growth is accepted as is. */
export const TOLERANCE = 1.08;
/** Share of the mean a faction may claim during capped growth. */
const CAP = 1.05;

/**
 * Territories for fixed capitals. `ratio` = max/min territory value, the balance achieved
 * (index.ts re-centres capitals and regrows when it stays above TOLERANCE).
 */
export function growTerritories(
  ctx: MapContext,
  capitals: readonly number[],
): { owner: Uint8Array; ratio: number } {
  const { grid, n, terrain } = ctx;
  const factions = capitals.length;
  const land = new Uint8Array(n);
  const worth = new Float64Array(n);
  let total = 0;
  for (let h = 0; h < n; h++) {
    if (!isLand(terrain[h])) continue;
    land[h] = 1;
    // Half raw area, half usable value: equal-looking *and* equally useful territories.
    worth[h] = 0.5 + 0.5 * LAND_VALUE[terrain[h]];
    total += worth[h];
  }
  const mean = total / factions;
  const cost = borderCost(ctx);
  const weights = new Array<number>(factions).fill(1);
  let best: Int32Array = new Int32Array(n);
  let bestWeights = weights.slice();
  let bestRatio = Infinity;
  let gain = 0.7;
  for (let iter = 0; iter < 40 && bestRatio > TOLERANCE; iter++) {
    const { label } = growRegions(grid, capitals, weights, cost, land);
    const value = territoryValues(label, worth, factions);
    const ratio = Math.max(...value) / Math.max(1e-9, Math.min(...value));
    if (ratio < bestRatio) {
      bestRatio = ratio;
      best = label;
      bestWeights = weights.slice();
    }
    // Too much land ⇒ grow slower next time (and vice versa). Damped linear update, clamped.
    for (let f = 0; f < factions; f++) {
      const w = weights[f] * (1 + gain * (value[f] / mean - 1));
      weights[f] = Math.min(2.5, Math.max(0.4, w));
    }
    gain *= 0.95;
  }
  // A chunk of land behind a chokepoint goes wholesale to whoever reaches the gap first, which no
  // weighting can split. Then cap every faction near its fair share and let the others take the rest.
  if (bestRatio > TOLERANCE) {
    const capped = growCapped(ctx, capitals, bestWeights, cost, land, worth, CAP * mean);
    const value = territoryValues(capped, worth, factions);
    const ratio = Math.max(...value) / Math.max(1e-9, Math.min(...value));
    if (ratio < bestRatio) {
      best = capped;
      bestRatio = ratio;
    }
  }
  const owner = new Uint8Array(n).fill(NO_FACTION);
  for (let h = 0; h < n; h++) if (best[h] >= 0) owner[h] = best[h];
  return { owner, ratio: bestRatio };
}

function territoryValues(label: Int32Array, worth: Float64Array, factions: number): number[] {
  const value = new Array<number>(factions).fill(0);
  for (let h = 0; h < label.length; h++) if (label[h] >= 0) value[label[h]] += worth[h];
  return value;
}

/**
 * Weighted growth in which a faction stops claiming once its territory is worth `cap`. Each faction
 * keeps its own distance field (entries are faction×hex), so a hex a capped faction refuses is still
 * reachable by the next-closest one. Land only a capped faction can reach is attached afterwards to
 * the neighbouring territory. Every claim extends an existing territory, so all stay contiguous.
 */
function growCapped(
  ctx: MapContext,
  capitals: readonly number[],
  weights: readonly number[],
  cost: StepCost,
  land: Uint8Array,
  worth: Float64Array,
  cap: number,
): Int32Array {
  const { grid, n } = ctx;
  const factions = capitals.length;
  const dist = new Float64Array(factions * n).fill(Infinity);
  const label = new Int32Array(n).fill(-1);
  const claimed = new Float64Array(factions);
  const heap = new MinHeap(n * 2);
  capitals.forEach((c, f) => {
    dist[f * n + c] = 0;
    heap.push(f * n + c, 0);
  });
  while (heap.size > 0) {
    const id = heap.pop();
    const key = heap.lastKey;
    if (key > dist[id]) continue;
    const f = Math.floor(id / n);
    const h = id - f * n;
    if (label[h] >= 0 || (claimed[f] + worth[h] > cap && h !== capitals[f])) continue;
    label[h] = f;
    claimed[f] += worth[h];
    for (let d = 0; d < 6; d++) {
      const m = grid.neighbour(h, d);
      if (m < 0 || !land[m] || label[m] >= 0) continue;
      const nd = key + cost(h, m, d) * weights[f];
      if (nd < dist[f * n + m]) {
        dist[f * n + m] = nd;
        heap.push(f * n + m, nd);
      }
    }
  }
  // Leftovers (reachable only through capped territory): attach breadth-first to a neighbour.
  const queue: number[] = [];
  for (let h = 0; h < n; h++) if (label[h] >= 0) queue.push(h);
  for (let i = 0; i < queue.length; i++) {
    const h = queue[i];
    grid.forNeighbours(h, (m) => {
      if (land[m] && label[m] < 0) {
        label[m] = label[h];
        queue.push(m);
      }
    });
  }
  return label;
}
